/* eslint-disable react-refresh/only-export-components -- cva variants co-located with component */
import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

export const selectTriggerVariants = cva(
  'flex items-center justify-between whitespace-nowrap text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
  {
    variants: {
      variant: {
        filter: 'h-9 gap-1.5 px-3 py-2 rounded bg-surface-high text-text-muted hover:bg-surface-muted hover:text-text',
        inline: 'h-9 gap-1 px-2.5 py-1.5 rounded w-full bg-surface-high text-text border border-input-border focus:border-primary focus:ring-1 focus:ring-primary',
        form: 'h-9 gap-1.5 px-3 py-2 rounded w-full bg-surface-elevated text-text border border-input-border focus:border-primary focus:ring-1 focus:ring-primary',
        default: 'h-9 gap-1.5 px-3 py-2 rounded w-full bg-surface-high text-text border border-input-border focus:border-primary focus:ring-1 focus:ring-primary',
      },
    },
    defaultVariants: { variant: 'default' },
  }
)

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, children, variant, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ variant }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-3.5 w-3.5 opacity-40 shrink-0" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4 text-text-subtle" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4 text-text-subtle" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-80 min-w-[8rem] overflow-hidden',
        'rounded-lg bg-surface-elevated text-text shadow-ambient',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1.5',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2.5 py-1.5 text-label-sm text-text-subtle', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center',
      'rounded-sm py-2 pl-8 pr-3 text-sm outline-none',
      'text-text-muted',
      'focus:bg-primary-subtle focus:text-text',
      'data-[state=checked]:text-primary data-[state=checked]:font-medium',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className
    )}
    {...props}
  >
    <span className="absolute left-2.5 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5 text-primary" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-surface-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

/* ─────────────────────────────────────────────────────────────
   FilterSelect — Convenience wrapper for common select pattern.
   Replaces native <select> across the app.
   ───────────────────────────────────────────────────────────── */

interface FilterSelectOption {
  value: string
  label: string
  /** Secondary text shown right-aligned, small + italic + muted (e.g. a type). */
  hint?: string
}

interface FilterSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: FilterSelectOption[]
  placeholder?: string
  allLabel?: string
  variant?: 'default' | 'filter' | 'inline' | 'form'
  className?: string
  disabled?: boolean
  id?: string
  /**
   * Accessible name for the trigger (ENT-445 F-049). The visible `allLabel` is placeholder CONTENT,
   * not a label, so without this the control is exposed to assistive tech as an unnamed combobox —
   * which is what put three unnamed comboboxes on the form that raises money. Supply it whenever
   * there is no visible <label> associated with the trigger's `id`.
   */
  ariaLabel?: string
  /**
   * Label for a `value` that is not among `options`, e.g. a tax rate carried on a record that is
   * not one of the standard choices offered here.
   *
   * Radix renders an EMPTY trigger for a value it has no item for — the control then shows nothing
   * while the value is still live in state, still driving totals, still submitted. Pass this and
   * the value is shown as an extra item instead, labelled by this function. It is inserted in place
   * when the list is numeric and ascending, appended otherwise — the caller's own options are never
   * reordered. The choosable list is otherwise untouched: the extra item exists only while that
   * value is selected.
   *
   * Opt-in on purpose — omit it and behaviour is exactly as before, so no existing caller changes.
   */
  unlistedValueLabel?: (value: string) => string
  'data-testid'?: string
}

/**
 * Put an unlisted value where a reader would look for it, WITHOUT reordering the caller's list.
 *
 * The first version sorted `[...options, extra]` numerically, which silently re-sorted the caller's
 * own options too: a descending `['2025','2024','2023']` plus an unlisted `'2019'` came back
 * ascending. That contradicted this feature's own promise to leave the offered list alone — true of
 * membership, false of order — so insertion is now strictly additive: the caller's order is
 * preserved in every case.
 *
 * In place only when the list is numeric AND already ascending (rates, percentages, counts — where
 * a reader scans for the value's position); appended otherwise (descending, unsorted, or opaque
 * values like ids and slugs), because there is no position that can be inferred without guessing.
 */
function withUnlistedValue(
  options: FilterSelectOption[],
  extra: FilterSelectOption,
): FilterSelectOption[] {
  const nums = options.map((o) => Number(o.value))
  const ascendingNumeric =
    options.every((o, i) => o.value !== '' && Number.isFinite(nums[i])) &&
    nums.every((n, i) => i === 0 || nums[i - 1] <= n) &&
    Number.isFinite(Number(extra.value))
  if (!ascendingNumeric) return [...options, extra]
  const at = nums.findIndex((n) => n > Number(extra.value))
  return at === -1
    ? [...options, extra]
    : [...options.slice(0, at), extra, ...options.slice(at)]
}

function FilterSelect({
  value,
  onValueChange,
  options,
  placeholder,
  allLabel,
  variant = 'filter',
  className,
  disabled,
  id,
  ariaLabel,
  unlistedValueLabel,
  'data-testid': testId,
}: FilterSelectProps) {
  const isUnlisted =
    Boolean(unlistedValueLabel) &&
    value !== '' &&
    value !== 'All' &&
    !options.some((opt) => opt.value === value)
  const shownOptions = isUnlisted
    ? withUnlistedValue(options, { value, label: unlistedValueLabel!(value) })
    : options
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger variant={variant} className={className} data-testid={testId} id={id} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder ?? allLabel ?? 'Select...'} />
      </SelectTrigger>
      <SelectContent>
        {allLabel && (
          <SelectItem value="All">{allLabel}</SelectItem>
        )}
        {shownOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.hint ? (
              <span className="flex items-center justify-between gap-4 w-full">
                <span className="truncate">{opt.label}</span>
                <span className="shrink-0 text-[11px] italic text-text-subtle">{opt.hint}</span>
              </span>
            ) : (
              opt.label
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  FilterSelect,
}
